/**
 * StoryBoard — top-level Visual Story Board screen. Guards for a missing
 * manuscript (same pattern as StoryEditor), owns the Board/Timeline tab and
 * character-filter state, and branches between the full react-flow canvas
 * (desktop/tablet) and the list fallback (mobile) for the Board tab.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, ListTree } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cx } from '@/components/site/primitives';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useMyWriting } from '@/components/myWriting';
import { useStoryBoard } from './context/StoryBoardContext';
import BoardCanvas from './BoardCanvas';
import BoardMobileList from './BoardMobileList';
import TimelineView from './TimelineView';
import CharacterFilterBar from './CharacterFilterBar';
import NodeDetailPanel from './NodeDetailPanel';
import { BOARD_EMPTY_MESSAGE } from './constants';

export default function StoryBoard() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { getStory } = useMyWriting();
  const { getBoard } = useStoryBoard();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isTablet = useMediaQuery('(min-width: 768px)');

  const [view, setView] = useState('board');
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState([]);

  const story = getStory(storyId);
  const board = getBoard(storyId);

  if (!story) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 text-center">
        <p className="font-display text-xl font-semibold text-ink">Manuscript not found.</p>
        <button type="button" onClick={() => navigate('/app/studio')} className="text-sm font-medium text-coral hover:underline">
          ← Back to Studio
        </button>
      </div>
    );
  }

  const characters = board.nodes.filter((n) => n.type === 'character');
  const selectedNode = board.nodes.find((n) => n.id === selectedNodeId) ?? null;

  const toggleCharacter = (id) => {
    setSelectedCharacterIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  return (
    <div className="flex h-screen flex-col bg-cream" data-testid="story-board">
      <header className="flex flex-wrap items-center gap-3 border-b border-line bg-card px-4 py-3 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(`/app/studio/${storyId}`)}
          className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-subtle hover:text-coral"
        >
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Back to Manuscript</span>
        </button>

        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-lg font-semibold text-ink">{story.title}</p>
          <p className="text-xs text-subtle">Story Board</p>
        </div>

        <Tabs value={view} onValueChange={setView} className="shrink-0">
          <TabsList className="h-auto rounded-full border border-line bg-cream p-1">
            <TabsTrigger value="board" className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold data-[state=active]:bg-coral-fill data-[state=active]:text-[#FFF7F0]">
              <LayoutGrid className="h-3.5 w-3.5" /> Board
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold data-[state=active]:bg-coral-fill data-[state=active]:text-[#FFF7F0]">
              <ListTree className="h-3.5 w-3.5" /> Timeline
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </header>

      {isTablet && characters.length > 0 && (
        <div className="border-b border-line bg-card/60 px-4 py-2.5 sm:px-6">
          <CharacterFilterBar
            characters={characters}
            selectedIds={selectedCharacterIds}
            onToggle={toggleCharacter}
            onClear={() => setSelectedCharacterIds([])}
          />
        </div>
      )}

      <main className="relative flex-1 overflow-hidden">
        {view === 'timeline' ? (
          <div className="h-full overflow-y-auto">
            <TimelineView story={story} board={board} onOpenNode={setSelectedNodeId} />
          </div>
        ) : isTablet ? (
          <>
            <BoardCanvas
              storyId={storyId}
              board={board}
              selectedCharacterIds={selectedCharacterIds}
              onOpenNode={setSelectedNodeId}
              simplified={!isDesktop}
            />
            {board.nodes.length === 0 && (
              <div className={cx('pointer-events-none absolute inset-0 flex items-center justify-center')}>
                <div className="max-w-xs text-center">
                  <p className="font-display text-lg font-semibold text-ink">{BOARD_EMPTY_MESSAGE.heading}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-subtle">{BOARD_EMPTY_MESSAGE.subheading}</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <BoardMobileList
            storyId={storyId}
            board={board}
            selectedCharacterIds={selectedCharacterIds}
            onToggleCharacter={toggleCharacter}
            onClearCharacters={() => setSelectedCharacterIds([])}
            onOpenNode={setSelectedNodeId}
          />
        )}
      </main>

      <NodeDetailPanel
        storyId={storyId}
        node={selectedNode}
        isOpen={!!selectedNode}
        onClose={() => setSelectedNodeId(null)}
      />
    </div>
  );
}
